import {
  collection,
  deleteDoc,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QuerySnapshot,
  runTransaction,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../config/firebase";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createReviewProps,
  ReviewWizardFormType,
} from "@beyondrealityapp/core/review/types";
import { ErrorMessage } from "@beyondrealityapp/core/shared/constants";
import { getCountFromServer } from "firebase/firestore";
import {
  GoalField,
  GoalHistorySource,
} from "@beyondrealityapp/core/goal/constants";
import { GoalClass } from "@beyondrealityapp/core/goal/classes";
import { GoalType } from "@beyondrealityapp/core/goal/types";
import { GoalStatus } from "@beyondrealityapp/core/goal/constants";
import { SharedUserSummary } from "@beyondrealityapp/core/shared/types";
import {
  Model,
  Operator,
} from "@beyondrealityapp/core/shared/constants";
import { ReviewClass } from "@beyondrealityapp/core/review/classes";
import {
  ReviewFirestoreType,
  ReviewFormType,
  createLifeReviewProps,
} from "@beyondrealityapp/core/review/types";
import { Filter } from "../utils/filter/classes";
import {
  ReviewField,
  ReviewVariant,
} from "@beyondrealityapp/core/review/constants";

export type updateReviewSharingProps = {
  id: string;
  sharedWithUIDs: string[];
  sharedWithUsers: SharedUserSummary[];
};

// How many recent reviews to scan for the previous life review
const PREVIOUS_LIFE_REVIEW_LOOKBACK = 25;


export const reviewApi = createApi({
  reducerPath: "reviewApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Review"],
  endpoints: (builder) => ({
    getCountGoals: builder.query({
      queryFn: async (filter: ReviewWizardFormType) => {
        try {
          const count = await _getCountGoalsCount(filter.themes, filter.states);
          return { data: count };
        } catch (error) {
          return { error: error as Error };
        }
      },
    }),
    fetchReviews: builder.query({
      queryFn: async (filter: Filter) => {
        try {
          const reviews = await _fetchReviews(filter);
          return { data: reviews };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: ["Review"],
    }),
    fetchReview: builder.query({
      queryFn: async (reviewId: string) => {
        try {
          const review = await _fetchReview(reviewId);
          return { data: review };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: ["Review"],
    }),
    createReview: builder.mutation({
      queryFn: async (
        props: createReviewProps = { type: "periodic", states: ["In progress"] }
      ) => {
        try {
          const review = await _createReview(props);
          return { data: review };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["Review"],
    }),
    createLifeReview: builder.mutation({
      queryFn: async (props: createLifeReviewProps) => {
        try {
          const review = await _createLifeReview(props);
          return { data: review };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["Review"],
    }),
    updateLifeReview: builder.mutation({
      queryFn: async (reviewForm: ReviewFormType) => {
        try {
          await _updateLifeReview(reviewForm);
          return { data: reviewForm.id };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["Review"],
    }),
    fetchPreviousLifeReview: builder.query({
      queryFn: async () => {
        try {
          const uid = auth.currentUser?.uid;
          if (!uid) throw new Error(ErrorMessage.NOT_AUTHORIZED);
          const review = await _fetchPreviousLifeReview(uid);
          return { data: review ?? null };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: ["Review"],
    }),
    updateReviewSharing: builder.mutation({
      queryFn: async (props: updateReviewSharingProps) => {
        try {
          await _updateReviewSharing(props);
          return { data: props.id };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["Review"],
    }),
    updateReview: builder.mutation({
      queryFn: async (reviewForm: Partial<ReviewFormType>) => {
        try {
          const review = await _updateReview(reviewForm);
          return { data: review };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["Review"],
    }),
    deleteReview: builder.mutation({
      queryFn: async (reviewId: string) => {
        try {
          await _deleteReview(reviewId);
          return { data: reviewId };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["Review"],
    }),
  }),
});

export const {
  useGetCountGoalsQuery,
  useFetchReviewsQuery,
  useFetchReviewQuery,
  useCreateReviewMutation,
  useCreateLifeReviewMutation,
  useUpdateLifeReviewMutation,
  useUpdateReviewMutation,
  useFetchPreviousLifeReviewQuery,
  useUpdateReviewSharingMutation,
  useDeleteReviewMutation,
} = reviewApi;

const _fetchReviews = (filter: Filter) => {
  const q = filter.createQuery(db);
  
  return new Promise<ReviewClass[]>((resolve, reject) => {
    getDocs(q)
      .then((querySnapshot) => {
        const reviews: ReviewClass[] = [];
        querySnapshot.forEach((doc: DocumentData) => {
          const review = ReviewClass.fromFirestore(
            doc.data() as ReviewFirestoreType
          );
          reviews.push(review);
        });
        resolve(reviews);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _fetchReview = (id: string) => {
  return new Promise<ReviewClass>((resolve, reject) => {
    getDoc(doc(db, Model.REVIEWS, id)).then((doc) => {
      if (doc.exists()) {
        const review = ReviewClass.fromFirestore(
          doc.data() as ReviewFirestoreType
        );
        resolve(review);
      } else {
        reject(ErrorMessage.NOT_FOUND);
      }
    });
  });
};

const _createReview = async (
  props: createReviewProps = { type: "periodic", states: ["In progress"] }
) => {
  let goalsFieldFilters = [];

  if (props.type === "thematic" && props.themes.length > 0) {
    const themesFilter = where(
      GoalField.THEMES,
      Operator.ARRAY_CONTAINS_ANY,
      props.themes
    );
    goalsFieldFilters.push(themesFilter);
  }
  if (props.states.length > 0) {
    const statesFilter = where(GoalField.STATUS, Operator.IN, props.states);
    goalsFieldFilters.push(statesFilter);
  }

  const q = query(
    collection(db, Model.GOALS),
    where(GoalField.UID, Operator.EQUAL_TO, auth.currentUser?.uid),
    ...goalsFieldFilters
  );

  const goalsQuerySnapshot = await getDocs(q);

  return new Promise<ReviewClass>((resolve, reject) => {
    _generateReview(db, goalsQuerySnapshot, props.type, props.sharedWithUIDs)
      .then((review) => {
        resolve(review);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _generateReview = async (
  db: Firestore,
  goalsQuerySnapshot: QuerySnapshot,
  type: "periodic" | "thematic",
  sharedWithUIDs?: string[]
) => {
  return new Promise<ReviewClass>((resolve, reject) => {
    if (goalsQuerySnapshot.empty) {
      reject(ErrorMessage.NOT_FOUND);
    }
    const review = new ReviewClass({
      type,
      reviewedGoals: [],
      sharedWithUIDs: sharedWithUIDs ?? [],
    });
    review.init(auth);

    goalsQuerySnapshot.forEach((doc) => {
      review.appendReviewedGoal(doc.id, doc.data() as GoalType);
    });

    setDoc(doc(db, Model.REVIEWS, review.id), review.toFirestore()).then(() => {
      resolve(review);
    });
  });
};

const _fetchPreviousLifeReview = async (uid: string) => {
  const q = query(
    collection(db, Model.REVIEWS),
    where(ReviewField.UID, Operator.EQUAL_TO, uid),
    orderBy(ReviewField.CREATED_AT, "desc"),
    limit(PREVIOUS_LIFE_REVIEW_LOOKBACK)
  );
  const snapshot = await getDocs(q);
  const previous = snapshot.docs
    .map((d) => d.data() as ReviewFirestoreType)
    .find((data) => data.type === ReviewVariant.LIFE);
  return previous ? ReviewClass.fromFirestore(previous) : undefined;
};

const _createLifeReview = async (props: createLifeReviewProps) => {
  const uid = auth.currentUser?.uid;
  if (!uid) {
    throw new Error(ErrorMessage.NOT_AUTHORIZED);
  }
  const previous = await _fetchPreviousLifeReview(uid);
  const review = ReviewClass.fromThemes(props.themes, previous);
  review.sharedWithUIDs = props.sharedWithUIDs ?? [];
  review.sharedWithUsers = props.sharedWithUsers ?? [];
  review.init(auth);
  await setDoc(doc(db, Model.REVIEWS, review.id), review.toFirestore());
  return review;
};

const _updateLifeReview = async (reviewForm: ReviewFormType) => {
  const review = ReviewClass.fromForm(reviewForm);
  review.setUpdatedTimestamp();
  // Saving happens only on submit, so a saved review is a completed one
  review.complete();
  await updateDoc(doc(db, Model.REVIEWS, review.id), {
    reviewedThemes: review.reviewedThemes,
    satisfactionScale: review.satisfactionScale,
    status: review.status,
    "metaData.updatedAt": review.metaData.updatedAt,
  });
};

const _updateReview = async (review: Partial<ReviewFormType>) => {
  const updatedReview = ReviewClass.fromForm(review);
  updatedReview.setUpdatedTimestamp();
  // Saving happens only on submit, so a saved review is a completed one
  updatedReview.complete();
  const reviewedAt = new Date();

  await runTransaction(db, async (transaction) => {
    // Firestore transactions need every read before the first write
    const goalSnapshots = await Promise.all(
      updatedReview.reviewedGoals.map((reviewedGoal) =>
        transaction.get(doc(db, Model.GOALS, reviewedGoal.id))
      )
    );

    updatedReview.reviewedGoals.forEach((reviewedGoal, index) => {
      const snapshot = goalSnapshots[index];
      // A goal deleted since the review started has nothing to update
      if (!snapshot.exists()) return;

      const goal = snapshot.data();
      const goalUpdate: { [field: string]: string | Date | null | unknown[] } = {};
      const childUpdate: { [field: string]: string } = {};

      if (reviewedGoal.newCurrent !== undefined) {
        goalUpdate.current = reviewedGoal.newCurrent;
        childUpdate.current = reviewedGoal.newCurrent;
      }
      if (reviewedGoal.newStatus && reviewedGoal.newStatus !== goal.status) {
        goalUpdate.status = reviewedGoal.newStatus;
        goalUpdate.completedAt =
          reviewedGoal.newStatus === GoalStatus.COMPLETED ? new Date() : null;
        childUpdate.status = reviewedGoal.newStatus;
      }
      // Every reviewed goal gets a data point, changed or not
      goalUpdate.history = [
        ...(goal.history ?? []),
        GoalClass.historyEntry({
          date: reviewedAt,
          current: reviewedGoal.newCurrent ?? goal.current ?? "",
          status: reviewedGoal.newStatus || goal.status || "",
          source: GoalHistorySource.REVIEW,
          satisfaction: reviewedGoal.satisfaction,
          reviewId: updatedReview.id,
        }),
      ];

      transaction.update(snapshot.ref, goalUpdate);
      // Parents keep a summary of each child goal
      if (goal.parent && Object.keys(childUpdate).length > 0) {
        transaction.set(
          doc(db, Model.GOALS, goal.parent),
          { children: { [reviewedGoal.id]: childUpdate } },
          { merge: true }
        );
      }
    });

    transaction.update(doc(db, Model.REVIEWS, updatedReview.id), {
      reviewedGoals: updatedReview.reviewedGoals,
      satisfactionScale: updatedReview.satisfactionScale,
      status: updatedReview.status,
      "metaData.updatedAt": updatedReview.metaData?.updatedAt,
    });
  });
};

const _updateReviewSharing = async ({
  id,
  sharedWithUIDs,
  sharedWithUsers,
}: updateReviewSharingProps) => {
  await updateDoc(doc(db, Model.REVIEWS, id), {
    sharedWithUIDs,
    sharedWithUsers,
    "metaData.updatedAt": new Date(),
  });
};

const _deleteReview = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    deleteDoc(doc(db, Model.REVIEWS, id))
      .then(() => {
        resolve();
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
};

const _getCountGoalsCount = async (
  themes: string[] = [],
  states: string[] = []
) => {
  return new Promise<number>((resolve, reject) => {
    let goalsFieldFilters = [];

    if (themes.length > 0) {
      const themesFilter = where(
        GoalField.THEMES,
        Operator.ARRAY_CONTAINS_ANY,
        themes
      );
      goalsFieldFilters.push(themesFilter);
    }
    if (states.length > 0) {
      const statesFilter = where(GoalField.STATUS, Operator.IN, states);
      goalsFieldFilters.push(statesFilter);
    }

    const q = query(
      collection(db, Model.GOALS),
      where(GoalField.UID, Operator.EQUAL_TO, auth.currentUser?.uid),
      ...goalsFieldFilters
    );
    getCountFromServer(q)
      .then((snapshot) => {
        resolve(snapshot.data().count);
      })
      .catch((error) => {
        reject(error);
      });
  });
};
